/**
 * 광고·제휴·사이드 통합 문의 페이지 (사용자 피드백 2026-05-22).
 *
 * BottomNavigation 의 "광고·제휴 문의" 클릭 시 진입.
 * 드롭다운: 사이드 / 광고 / 제휴
 * 입력: 이름 / 연락처 / 내용
 * 제출 시 POST /api/properties/business-inquiry/ → lwkmlb@naver.com 메일 발송.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

type InquiryType = 'side' | 'ad' | 'partner';

const TYPE_LABELS: Record<InquiryType, string> = {
  side: '사이드 문의',
  ad: '광고 문의',
  partner: '제휴 문의',
};

const BusinessInquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const [type, setType] = useState<InquiryType>('ad');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      alert('이름과 연락처를 입력해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      // services/api 인스턴스의 baseURL 이 이미 '/api' 를 포함하므로 경로에 '/api' 를 중복하지 않는다.
      await api.post('/properties/business-inquiry/', {
        type, name, phone, message,
      });
      setDone(true);
    } catch (err: any) {
      alert(err?.response?.data?.error || '전송 실패. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-paper">
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-6 py-10 text-center">
        <div className="text-5xl">📨</div>
        <h1 className="mt-4 text-[18px] font-extrabold text-ink">{TYPE_LABELS[type]} 접수 완료</h1>
        <p className="mt-2 text-[13px] text-muted">
          담당자 확인 후 빠른 시일 내 연락드리겠습니다.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 rounded-ca-md bg-accent px-5 py-3 text-[13px] font-extrabold text-white shadow-ca-cta"
        >
          홈으로
        </button>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
    <div className="mx-auto max-w-md px-4 py-6 md:px-6 md:py-8">
      <div className="mb-5">
        <h1 className="text-[18px] font-extrabold text-ink">광고·제휴 문의</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          부냥 광고 / 제휴 / 사이드 문의를 접수합니다. 영업일 기준 1~2일 내 회신드립니다.
        </p>
      </div>

      <form
        method="post"
        onSubmit={onSubmit}
        className="flex flex-col gap-3 rounded-ca-2xl border border-ca-line bg-frame p-5"
      >
        <div className="flex flex-col gap-2">
          <label className="text-[11.5px] font-bold text-muted">
            문의 유형 <span className="text-accent">*</span>
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as InquiryType)}
            disabled={submitting}
            className="rounded-ca-md border border-ca-line bg-white px-3.5 py-3 text-[14px] text-ink outline-none focus:border-accent disabled:opacity-60"
          >
            <option value="ad">광고 문의</option>
            <option value="partner">제휴 문의</option>
            <option value="side">사이드 문의</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[11.5px] font-bold text-muted">
            이름 <span className="text-accent">*</span>
          </label>
          <input
            type="text"
            placeholder="이름을 입력하세요"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={submitting}
            className="rounded-ca-md border border-ca-line bg-white px-3.5 py-3 text-[14px] text-ink outline-none placeholder:text-mute2 focus:border-accent disabled:opacity-60"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[11.5px] font-bold text-muted">
            연락처 <span className="text-accent">*</span>
          </label>
          <input
            type="tel"
            inputMode="tel"
            placeholder="010-0000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            disabled={submitting}
            className="rounded-ca-md border border-ca-line bg-white px-3.5 py-3 text-[14px] tabular-nums text-ink outline-none placeholder:text-mute2 focus:border-accent disabled:opacity-60"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[11.5px] font-bold text-muted">
            내용
          </label>
          <textarea
            placeholder="문의 내용을 입력하세요"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            disabled={submitting}
            className="resize-none rounded-ca-md border border-ca-line bg-white px-3.5 py-3 text-[14px] text-ink outline-none placeholder:text-mute2 focus:border-accent disabled:opacity-60"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-2 rounded-ca-md bg-accent px-4 py-3.5 text-[14px] font-extrabold text-white shadow-ca-cta transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? '전송 중…' : '문의하기'}
        </button>
      </form>
    </div>
    </div>
  );
};

export default BusinessInquiryPage;
